/**
 * GuestCompletionSection Component
 * Allows external user to mark their work as complete with optional note
 */
import React, { useState } from 'react';
import { Card, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { CheckCircle, Undo2, AlertTriangle } from 'lucide-react';
import { formatDateTimeForDisplay } from '../../../Helpers/DateUtils';

const MAX_NOTE_LENGTH = 1000;

interface GuestCompletionSectionProps {
    completedAt: string | null;
    completedNote: string | null;
    onSetCompletion: (completed: boolean, note?: string) => Promise<void>;
    submitting: boolean;
    /**
     * Number of linked work orders ("delmoment") still open. The project cannot
     * be marked complete until this is 0 — the server enforces the same gate.
     */
    openWorkOrderCount: number;
    /** Completed project: the guest may only undo their own completion signal. */
    readOnly?: boolean;
}

const GuestCompletionSection: React.FC<GuestCompletionSectionProps> = ({
    completedAt,
    completedNote,
    onSetCompletion,
    submitting,
    openWorkOrderCount,
    readOnly = false,
}) => {
    const [note, setNote] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleMarkComplete = async () => {
        setLocalError(null);
        setIsSubmitting(true);
        try {
            await onSetCompletion(true, note.trim() || undefined);
            setNote(''); // Clear note after successful submission
        } catch (err) {
            // Prefer the server message (e.g. the work-order gate) over a generic one.
            setLocalError(
                err instanceof Error && err.message
                    ? err.message
                    : 'Kunde inte markera som klar. Försök igen.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUndo = async () => {
        setLocalError(null);
        setIsSubmitting(true);
        try {
            await onSetCompletion(false);
        } catch {
            setLocalError('Kunde inte ångra markering. Försök igen.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isLoading = submitting || isSubmitting;
    const noteLength = note.length;
    const hasOpenWorkOrders = openWorkOrderCount > 0;

    // Already completed - show confirmation with undo option
    if (completedAt) {
        return (
            <Card className="guest-completion-section mb-4 border-success">
                <Card.Body className="text-center">
                    <div className="d-flex justify-content-center mb-3">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 64, height: 64, backgroundColor: '#d1e7dd' }}
                        >
                            <CheckCircle size={32} className="text-success" />
                        </div>
                    </div>
                    <h5 className="text-success mb-2">Arbetet är markerat som klart</h5>
                    <p className="text-muted small mb-2">
                        {formatDateTimeForDisplay(completedAt)}
                    </p>
                    {completedNote && (
                        <div className="bg-light rounded p-3 mb-3 text-start">
                            <small className="text-muted d-block mb-1">Din kommentar:</small>
                            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                {completedNote}
                            </p>
                        </div>
                    )}
                    {localError && (
                        <Alert variant="danger" className="mb-3">
                            {localError}
                        </Alert>
                    )}
                    <Button
                        variant="outline-secondary"
                        onClick={handleUndo}
                        disabled={isLoading}
                        className="d-inline-flex align-items-center gap-2"
                    >
                        {isLoading ? (
                            <Spinner size="sm" animation="border" />
                        ) : (
                            <Undo2 size={18} />
                        )}
                        Ångra markering
                    </Button>
                </Card.Body>
            </Card>
        );
    }

    if (readOnly) {
        return (
            <Card className="guest-completion-section mb-4">
                <Card.Body className="text-center">
                    <h5 className="mb-2">Projektet är avslutat</h5>
                    <p className="text-muted mb-0">
                        Förvaltaren har markerat projektet som utfört. Du kan fortfarande se det här i 30 dagar.
                    </p>
                </Card.Body>
            </Card>
        );
    }

    // Not completed - show completion form
    return (
        <Card className="guest-completion-section mb-4">
            <Card.Body className="text-center">
                <h5 className="mb-3">Är du klar med ditt arbete?</h5>
                <p className="text-muted mb-3">
                    Markera när du är färdig så ser förvaltaren det i systemet.
                </p>

                <Form.Group className="mb-3 text-start">
                    <Form.Label className="small text-muted">
                        Lägg till en kommentar (valfritt)
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="T.ex. 'Allt klart, ventilationen är testad och fungerar'"
                        maxLength={MAX_NOTE_LENGTH}
                        disabled={isLoading}
                    />
                    <div className="d-flex justify-content-end mt-1">
                        <small className={noteLength > MAX_NOTE_LENGTH * 0.9 ? 'text-warning' : 'text-muted'}>
                            {noteLength} / {MAX_NOTE_LENGTH}
                        </small>
                    </div>
                </Form.Group>

                {hasOpenWorkOrders && (
                    <Alert variant="warning" className="mb-3 d-flex align-items-start gap-2 text-start">
                        <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
                        <span>
                            {openWorkOrderCount === 1
                                ? 'Det finns ett ärende som inte är slutfört. Slutför det innan du markerar projektet som klart.'
                                : `Det finns ${openWorkOrderCount} ärenden som inte är slutförda. Slutför dem innan du markerar projektet som klart.`}
                        </span>
                    </Alert>
                )}

                {localError && (
                    <Alert variant="danger" className="mb-3">
                        {localError}
                    </Alert>
                )}

                <Button
                    variant="success"
                    size="lg"
                    onClick={handleMarkComplete}
                    disabled={isLoading || hasOpenWorkOrders}
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                >
                    {isLoading ? (
                        <Spinner size="sm" animation="border" />
                    ) : (
                        <CheckCircle size={20} />
                    )}
                    Markera som klar
                </Button>
            </Card.Body>
        </Card>
    );
};

export default GuestCompletionSection;
